import { SmartCardProvider } from '@atlaskit/link-provider';
import { Grid, GridColumn } from '@atlaskit/page';
import { when } from 'mobx';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { Suspense } from 'react';
import ReactResizeDetector from 'react-resize-detector';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';

import FeatureFlagsClient from '../clients/FeatureFlagsClient';
import { getConfig } from '../config';
import { ROUTE_HOME } from '../constants/routes';
import { PostOfficeContextProvider } from '../context/PostOfficeContextProvider';
import { AnalyticsConsumerProps, withAnalytics } from '../hoc/withAnalytics';
import { AUTH_FLOW, cardClient } from '../hooks/useCardClient';
import UchiIntlProvider from '../i18n/UchiIntlProvider';
import { stripParams } from '../routing';
import { captureFatal } from '../sentry';
import { ErrorSource } from '../sentry/types';
import Store from '../store';
import FeatureFlagsStore from '../store/featureFlagsStore';
import ProductStore from '../store/productStore';
import UIStore, { UILoadingState } from '../store/uiStore';
import UserStore from '../store/userStore';

import Application from './Application';
import Blanket from './Blanket';
import CrossFlowProvider from './cross-flow/CrossFlowProvider';
import CrossFlowWAC from './cross-flow/wac/CrossFlowWAC';
import Drawer from './Drawer';
import Flags from './Flags';
import HomeLayoutV3 from './Layout/v3';
import PageTitle from './PageTitle';

type InjectedProps = {
  featureFlagsStore: FeatureFlagsStore;
  productStore: ProductStore;
  uiStore: UIStore;
  userStore: UserStore;
} & AnalyticsConsumerProps;

const AsyncHeartbeat = React.lazy(() =>
  import(
    /* webpackChunkName: "heartbeat-deferred" */
    '@atlassian/heartbeat'
  ),
);

const INITIAL_THEME = {};

@observer
class Start extends React.Component<InjectedProps> {
  private atlOrigin: string = safeGetURLParam('atlOrigin');
  private featureFlagsClient?: FeatureFlagsClient;

  private utmSource: string = safeGetURLParam('utm_source');

  componentWillMount() {
    this.props.userStore.getUser();
    this.props.analytics.startUIViewedEvent();

    this.featureFlagsClient = new FeatureFlagsClient(
      this.props.userStore,
      this.props.productStore,
      this.props.analytics,
    );
    this.props.featureFlagsStore.fetchFlags(this.featureFlagsClient);

    this.setupProductsLoadingSubscriptions();
    this.onFeatureFlagsLoaded();
  }

  onFeatureFlagsLoaded = () => {
    const { featureFlagsStore, analytics } = this.props;
    when(
      () => featureFlagsStore.isDone,
      () => {
        if (featureFlagsStore.hasFailed) {
          analytics.trackOperational('featureFlagStore', 'failed');
        }
      },
    );
  };

  render() {
    const { featureFlagsStore } = this.props;

    return (
      <Router>
        <ThemeProvider theme={INITIAL_THEME}>
          <UchiIntlProvider>
            <PostOfficeContextProvider>
              <CrossFlowProvider>
                <SmartCardProvider client={cardClient} authFlow={AUTH_FLOW}>
                  <ReactResizeDetector handleWidth handleHeight onResize={this.onResize} />
                  <PageTitle />
                  <div>
                    <Blanket />
                    <Drawer />
                    <Flags />
                  </div>

                  <HomeLayoutV3>
                    <Switch>
                      <Route path={ROUTE_HOME}>
                        <Grid columns={16}>
                          <GridColumn key="main-content" medium={16}>
                            <Application atlOrigin={this.atlOrigin} utmSource={this.utmSource} />
                          </GridColumn>
                        </Grid>
                      </Route>
                    </Switch>
                  </HomeLayoutV3>
                </SmartCardProvider>
              </CrossFlowProvider>
            </PostOfficeContextProvider>
            {/* WAC via Start Cross Flow touch point support */}
            <CrossFlowWAC />
          </UchiIntlProvider>
        </ThemeProvider>
        <Suspense fallback={<div />}>
          <AsyncHeartbeat onAuthenticationFailed={() => (window.location.href = getConfig().atlassianAccountUrl)} />
        </Suspense>
      </Router>
    );
  }

  setupProductsLoadingSubscriptions = () => {
    const { productStore, userStore, uiStore, analytics } = this.props;
    productStore.prefetchProductsAndJoinableSites();

    when(
      () => productStore.isDone && userStore.isDone,
      () => {
        if (productStore.hasLoaded) {
          uiStore.setLoadingState({ products: UILoadingState.READY_TO_RENDER });
          analytics.trackOperational('productStore', 'loaded');
        } else if (productStore.hasFailed) {
          captureFatal(productStore.lastLoadError || new Error('unknown'), {
            error_type: 'sli-bad-event',
            source: ErrorSource.PRODUCT_STORE,
          });

          uiStore.setLoadingState({ products: UILoadingState.ERROR });
        }
      },
    );
  };

  private onResize = (width: number): void => {
    this.props.uiStore.setOnMobileMode(width);
  };
}

function safeGetURLParam(paramName: string, defaultValue: string = 'none'): string {
  return stripParams(paramName)[paramName] || defaultValue;
}

export default inject(({ productStore, featureFlagsStore, userStore, uiStore }: Store) => ({
  featureFlagsStore,
  productStore,
  uiStore,
  userStore,
}))(withAnalytics(Start));
